/* eslint-disable react-refresh/only-export-components */
import React, { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  useLocation,
  useRouteError
} from 'react-router';
import {
  LOGIN_PATH,
  ROLE_HOME,
  ROLE_SCOPE,
  Scope
} from '@link/admin/constants';
import { BaseLayout } from '@link/admin/layouts';
import { canManageStaff, getRole, isAuthenticated } from '@link/admin/services';

const Agencies = lazy(() => import('@link/admin/views/Agencies'));
const AgencyMerchants = lazy(() => import('@link/admin/views/AgencyMerchants'));
const AccountInfo = lazy(() => import('@link/admin/views/AccountInfo'));
const CustomerDetail = lazy(() => import('@link/admin/views/CustomerDetail'));
const Customers = lazy(() => import('@link/admin/views/Customers'));
const Dashboard = lazy(() => import('@link/admin/views/Dashboard'));
const DocumentDetail = lazy(() => import('@link/admin/views/DocumentDetail'));
const Documents = lazy(() => import('@link/admin/views/Documents'));
const Login = lazy(() => import('@link/admin/views/Login'));
const MerchantForm = lazy(() => import('@link/admin/views/MerchantForm'));
const Merchants = lazy(() => import('@link/admin/views/Merchants'));
const Notifications = lazy(() => import('@link/admin/views/Notifications'));
const ShopCategories = lazy(() => import('@link/admin/views/ShopCategories'));
const ShopForm = lazy(() => import('@link/admin/views/ShopForm'));
const Shops = lazy(() => import('@link/admin/views/Shops'));
const Staffs = lazy(() => import('@link/admin/views/Staffs'));

function RootErrorBoundary() {
  const error = useRouteError() as Error;
  if (error.message.includes('Failed to fetch dynamically imported module')) {
    location.reload();
  }
  return null;
}

const lazyView = (view: JSX.Element) => {
  return <Suspense fallback="Loading...">{view}</Suspense>;
};

const RequireScope: React.FC<{
  scope: Scope;
  children: React.ReactNode;
}> = ({ scope, children }) => {
  const location = useLocation();

  if (!isAuthenticated()) {
    return (
      <Navigate
        to={LOGIN_PATH}
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  const role = getRole();

  if (!role) {
    return <Navigate to={LOGIN_PATH} replace />;
  }

  if (ROLE_SCOPE[role] !== scope) {
    return <Navigate to={ROLE_HOME[role]} replace />;
  }

  return <>{children}</>;
};

const RequireStaffManager: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const role = getRole();

  if (!role) {
    return <Navigate to={LOGIN_PATH} replace />;
  }

  if (!canManageStaff()) {
    return <Navigate to={ROLE_HOME[role]} replace />;
  }

  return <>{children}</>;
};

const ConsoleRedirect = () => {
  const role = isAuthenticated() ? getRole() : undefined;

  return <Navigate to={role ? ROLE_HOME[role] : LOGIN_PATH} replace />;
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<RootErrorBoundary />}>
      <Route path={LOGIN_PATH} element={lazyView(<Login />)} />

      <Route
        path="/link"
        element={
          <RequireScope scope={Scope.Link}>
            <BaseLayout />
          </RequireScope>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={lazyView(<Dashboard />)} />
        <Route path="merchants" element={lazyView(<Merchants />)} />
        <Route path="merchants/new" element={lazyView(<MerchantForm />)} />
        <Route
          path="merchants/:merchantId/edit"
          element={lazyView(<MerchantForm />)}
        />
        <Route
          path="merchants/:merchantId/shops"
          element={lazyView(<Shops />)}
        />
        <Route
          path="merchants/:merchantId/shops/new"
          element={lazyView(<ShopForm />)}
        />
        <Route
          path="merchants/:merchantId/shops/:groupId/edit"
          element={lazyView(<ShopForm />)}
        />
        <Route
          path="merchants/:merchantId/shops/:groupId/categories"
          element={lazyView(<ShopCategories />)}
        />
        <Route path="documents" element={lazyView(<Documents />)} />
        <Route
          path="documents/:orderId"
          element={lazyView(<DocumentDetail />)}
        />
        <Route path="customers" element={lazyView(<Customers />)} />
        <Route
          path="customers/:passportNumber"
          element={lazyView(<CustomerDetail />)}
        />
        <Route path="agencies" element={lazyView(<Agencies />)} />
        <Route
          path="agencies/:agentId/merchants"
          element={lazyView(<AgencyMerchants />)}
        />
        <Route
          path="staffs"
          element={
            <RequireStaffManager>{lazyView(<Staffs />)}</RequireStaffManager>
          }
        />
        <Route path="notifications" element={lazyView(<Notifications />)} />
      </Route>

      <Route
        path="/merchant"
        element={
          <RequireScope scope={Scope.Merchant}>
            <BaseLayout />
          </RequireScope>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={lazyView(<Dashboard />)} />
        <Route path="accountinfo" element={lazyView(<AccountInfo />)} />
        <Route
          path="merchants/:merchantId/edit"
          element={lazyView(<MerchantForm />)}
        />
        <Route
          path="merchants/:merchantId/shops"
          element={lazyView(<Shops />)}
        />
        <Route
          path="merchants/:merchantId/shops/new"
          element={lazyView(<ShopForm />)}
        />
        <Route
          path="merchants/:merchantId/shops/:groupId/edit"
          element={lazyView(<ShopForm />)}
        />
        <Route
          path="merchants/:merchantId/shops/:groupId/categories"
          element={lazyView(<ShopCategories />)}
        />
        <Route path="documents" element={lazyView(<Documents />)} />
        <Route
          path="documents/:orderId"
          element={lazyView(<DocumentDetail />)}
        />
        <Route
          path="staffs"
          element={
            <RequireStaffManager>{lazyView(<Staffs />)}</RequireStaffManager>
          }
        />
      </Route>

      <Route
        path="/shop"
        element={
          <RequireScope scope={Scope.Shop}>
            <BaseLayout />
          </RequireScope>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={lazyView(<Dashboard />)} />
        <Route path="accountinfo" element={lazyView(<AccountInfo />)} />
        <Route
          path="staffs"
          element={
            <RequireStaffManager>{lazyView(<Staffs />)}</RequireStaffManager>
          }
        />
      </Route>

      <Route path="*" element={<ConsoleRedirect />} />
    </Route>
  )
);

export default router;
